/**
 * A connection's OAuth sign-in on the execution service: whether a spec needs it, the status check
 * `Atoms.OAuthButton` and the test step make, and the token sync the dataplane needs before a test.
 * Callers bring their own HTTP client and auth.
 */
import { ConditionTypes } from '../enums';
import { readSupportedProviders, type SupportedProviders } from './appRegistrations';
import { type ServiceReply, throwIfFailedReply } from './reply';

export const OAUTH_LOGIN_PATH = '/oauth/login';
export const OAUTH_TOKENS_PATH = '/oauth/tokens';
/** On the fabric's dataplane, not the execution service. */
export const OAUTH_SYNC_PATH = '/oauth/sync';

const EXECUTION_SERVICE_DEFAULT_SUBDOMAIN = 'execution.dp';

/** `prophecy.ui.execution.url` from the global config, or `execution.dp.<prophecy host>`. */
export function executionServiceHost(executionServiceUrl: string | undefined, prophecyHost: string): string {
  return executionServiceUrl
    ? executionServiceUrl.replace(':443', '').replace(/^https?:\/\//, '')
    : `${EXECUTION_SERVICE_DEFAULT_SUBDOMAIN}.${prophecyHost}`;
}

export interface OAuthLoginRequest {
  host: string;
  /** The test step sends the spec's resolved provider, which may be unset; the button falls back to the kind. */
  provider?: string;
  connectorKind: string;
  appRegistrationId?: string;
  supportedProviders?: SupportedProviders;
}

export interface OAuthLoginStatus {
  loginNeeded: boolean;
  loginUrl?: string;
}

export type OAuthLoginReply = ServiceReply<OAuthLoginStatus> | null | undefined;

export function readOAuthLoginStatus(reply: OAuthLoginReply): OAuthLoginStatus {
  throwIfFailedReply(reply);
  if (typeof reply?.data?.loginNeeded !== 'boolean') throw new Error('OAuth login status reply has no loginNeeded');
  return reply.data;
}

/** The test step's check (`checkOAuthLogin`). */
export function isOAuthLoggedIn(status: Pick<OAuthLoginStatus, 'loginNeeded'>): boolean {
  return !status.loginNeeded;
}

/** `Atoms.OAuthButton`'s check: a sign-in is asked for only with a url to sign in at; `''` reads as signed in. */
export function oAuthButtonLoginUrl(status: OAuthLoginStatus): string {
  return status.loginNeeded && status.loginUrl ? status.loginUrl : '';
}

/** The test step's request: the spec's resolved `oAuthProperties` as they are, with the kind's supported providers. */
export function oAuthTestLoginRequest(
  resolvedAuthProperties: Record<string, string>,
  defaultState: { metadata?: unknown } | undefined
): OAuthLoginRequest {
  return {
    host: resolvedAuthProperties.host,
    provider: resolvedAuthProperties.provider,
    connectorKind: resolvedAuthProperties.connectorKind,
    supportedProviders: readSupportedProviders(defaultState),
    appRegistrationId: resolvedAuthProperties.appRegistrationId
  };
}

/** `Atoms.OAuthButton`'s check: the provider falls back to the connector kind, and nothing is asked without a host. */
export function oAuthButtonLoginRequest({
  host,
  provider,
  connectorKind,
  appRegistrationId
}: {
  host?: string;
  provider?: string;
  connectorKind?: string;
  appRegistrationId?: string;
}): OAuthLoginRequest | undefined {
  const effectiveProvider = provider || connectorKind;
  if (!host || !effectiveProvider || !connectorKind) return undefined;
  return { host, provider: effectiveProvider, connectorKind, appRegistrationId };
}

export type EncryptedAccessTokensReply<T = unknown> = ServiceReply<{ tokens?: T[] }> | null | undefined;

export function readEncryptedAccessTokens<T>(reply: EncryptedAccessTokensReply<T>): T[] | undefined {
  throwIfFailedReply(reply);
  const tokens = reply?.data?.tokens;
  return Array.isArray(tokens) ? tokens : undefined;
}

/** The user's encrypted tokens from the execution service, copied to the fabric's dataplane; `false` on any failure. */
export async function retrieveAndSyncAccessTokens<T>(
  getTokens: () => Promise<EncryptedAccessTokensReply<T>>,
  syncTokens: (body: { tokens: T[] }) => Promise<unknown>
): Promise<boolean> {
  try {
    const tokens = readEncryptedAccessTokens(await getTokens());
    if (!tokens) return false;
    await syncTokens({ tokens });
    return true;
  } catch (error) {
    console.error('Failed to retrieve/sync tokens', error);
    return false;
  }
}

export interface PlainCondition {
  type?: string;
  test?: unknown;
  test_value?: unknown;
  test_list?: unknown[];
}

export function evalPlainCondition({ test, test_value, type, test_list }: PlainCondition): boolean {
  switch (type) {
    case ConditionTypes.EqualCondition:
      return test === test_value;
    case ConditionTypes.NotEqualCondition:
      return test !== test_value;
    case ConditionTypes.PresentInListCondition:
      return Boolean(test_value && test_list?.includes(test_value));
    default:
      return false;
  }
}

export interface ConnectionOAuthSpec<P> {
  properties: P;
  conditions?: P | P[];
}

/** A connection spec's `oAuthProperties`: does the form ask for OAuth, and with which login properties. */
export function getConnectionOAuthInfoFromSpec<P>(
  connectionOAuthSpec: ConnectionOAuthSpec<P>,
  getPropertiesFromSpec: (propertiesSpec: P) => Record<string, string>
): { isConditionMetForOAuth: boolean; resolvedAuthProperties: Record<string, string> } {
  let isConditionMetForOAuth = false;
  let resolvedAuthProperties: Record<string, string> = {};
  try {
    const { conditions, properties } = connectionOAuthSpec;
    if (conditions) {
      const list = Array.isArray(conditions) ? conditions : [conditions];
      isConditionMetForOAuth = list.every((condition) =>
        evalPlainCondition(getPropertiesFromSpec(condition) as unknown as PlainCondition)
      );
    }
    if (isConditionMetForOAuth) resolvedAuthProperties = getPropertiesFromSpec(properties);
  } catch (error) {
    console.error('failed to get connection oauth info from spec', error);
  }
  return { isConditionMetForOAuth, resolvedAuthProperties };
}
